import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import RankingTable from '@/components/RankingTable';
import RankingDestaques from '@/components/RankingDestaques';
import { Search, FileDown, Flame, Trophy, HelpCircle } from 'lucide-react';
import LoadMoreButton from '@/components/LoadMoreButton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { RegulamentoButton } from '@/components/RegulamentoModal';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RankingProfissional = () => {
  const navigate = useNavigate();
  const [categoriaAtual, setCategoriaAtual] = useState('masculino');
  const [rankingData, setRankingData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalAtletas, setTotalAtletas] = useState(0);
  const [showDestaques, setShowDestaques] = useState(true);

  const [filtroNome, setFiltroNome] = useState('');
  const [filtroColocacao, setFiltroColocacao] = useState('');
  const [filtroUF, setFiltroUF] = useState('');
  const [filtroFaixa, setFiltroFaixa] = useState('');
  const [filtroEquipe, setFiltroEquipe] = useState('');
  const [filtroCidade, setFiltroCidade] = useState('');
  const [faixasDisponiveis, setFaixasDisponiveis] = useState([]);
  const [equipesDisponiveis, setEquipesDisponiveis] = useState([]);

  useEffect(() => {
    const fetchAuxData = async () => {
      try {
        const [faixasRes, equipesRes] = await Promise.all([
          axios.get(`${API}/ranking/faixas-etarias`),
          axios.get(`${API}/ranking/equipes`)
        ]);
        setFaixasDisponiveis(faixasRes.data.faixas || []);
        setEquipesDisponiveis(equipesRes.data.equipes || []);
      } catch (error) {
        console.error('Erro ao buscar dados auxiliares:', error);
      }
    };
    fetchAuxData();
  }, []);

  useEffect(() => {
    setRankingData([]);
    setCurrentPage(1);
    setHasMore(false);
    fetchRankingPage(1, true);
  }, [categoriaAtual, filtroFaixa, filtroEquipe, filtroCidade]);

  const fetchRankingPage = async (page = 1, reset = false) => {
    if (reset) setLoading(true);
    else setLoadingMore(true);
    try {
      let url = `${API}/ranking/categoria/${categoriaAtual}/M?ano=${new Date().getFullYear()}&page=${page}&limit=20`;
      if (filtroFaixa) url += `&faixa=${filtroFaixa}`;
      if (filtroEquipe) url += `&equipe=${encodeURIComponent(filtroEquipe)}`;
      if (filtroCidade) url += `&cidade=${encodeURIComponent(filtroCidade)}`;
      const response = await axios.get(url);
      const data = response.data;
      const items = data.ranking || data;
      if (reset) {
        setRankingData(Array.isArray(items) ? items : []);
      } else {
        setRankingData(prev => [...prev, ...(Array.isArray(items) ? items : [])]);
      }
      setHasMore(data.has_more || false);
      setTotalAtletas(data.total_atletas || (Array.isArray(items) ? items.length : 0));
      setCurrentPage(page);
    } catch (error) {
      console.error('Erro ao buscar ranking:', error);
      if (reset) setRankingData([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => fetchRankingPage(currentPage + 1, false);

  const rankingFiltrado = rankingData.filter(atleta => {
    const nomeMatch = atleta.nome.toLowerCase().includes(filtroNome.toLowerCase());
    const colocacaoMatch = filtroColocacao === '' || atleta.colocacao === parseInt(filtroColocacao);
    const ufMatch = atleta.uf.toLowerCase().includes(filtroUF.toLowerCase());
    return nomeMatch && colocacaoMatch && ufMatch;
  });

  const handleAtletaClick = (atletaId) => navigate(`/atleta/${atletaId}`);
  const handleExport = (format) => window.open(`${API}/ranking/export/${format}?categoria=${categoriaAtual}`, '_blank');
  const limparFiltros = () => {
    setFiltroNome(''); setFiltroColocacao(''); setFiltroUF('');
    setFiltroFaixa(''); setFiltroEquipe(''); setFiltroCidade('');
  };

  return (
    <>
          <>
            {/* Tabs de Categorias */}
            <Card className="mb-6 border-slate-200 dark:border-slate-800 shadow-lg">
              <CardContent className="pt-6">
                <Tabs value={categoriaAtual} onValueChange={setCategoriaAtual} className="w-full">
                  <TabsList className="grid w-full grid-cols-2 lg:grid-cols-6 h-auto gap-1">
                    <TabsTrigger value="masculino" className="font-semibold py-3 text-xs lg:text-sm" data-testid="tab-masculino">
                      MASCULINO
                    </TabsTrigger>
                    <TabsTrigger value="feminino" className="font-semibold py-3 text-xs lg:text-sm" data-testid="tab-feminino">
                      FEMININO
                    </TabsTrigger>
                    <TabsTrigger value="pcd-m" className="font-semibold py-3 text-xs lg:text-sm" data-testid="tab-pcd-m">
                      PCD / M
                    </TabsTrigger>
                    <TabsTrigger value="pcd-f" className="font-semibold py-3 text-xs lg:text-sm" data-testid="tab-pcd-f">
                      PCD / F
                    </TabsTrigger>
                    <TabsTrigger value="cadeirante-m" className="font-semibold py-3 text-xs lg:text-sm" data-testid="tab-cadeirante-m">
                      CADEIRANTE / M
                    </TabsTrigger>
                    <TabsTrigger value="cadeirante-f" className="font-semibold py-3 text-xs lg:text-sm" data-testid="tab-cadeirante-f">
                      CADEIRANTE / F
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </CardContent>
            </Card>

            {/* Layout: Filtros + Tabela */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Sidebar de Filtros */}
              <Card className="lg:col-span-1 h-fit border-slate-200 dark:border-slate-800 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Search className="w-5 h-5" />
                    Opções de filtro
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
              {/* Filtro Nome */}
              <div>
                <Label htmlFor="filtro-nome" className="text-sm font-medium mb-2 block">
                  Nome
                </Label>
                <Input
                  id="filtro-nome"
                  placeholder="Buscar por nome"
                  value={filtroNome}
                  onChange={(e) => setFiltroNome(e.target.value)}
                  data-testid="filtro-nome"
                />
              </div>

              {/* Filtro Colocação */}
              <div>
                <Label htmlFor="filtro-colocacao" className="text-sm font-medium mb-2 block">
                  Colocação
                </Label>
                <Input
                  id="filtro-colocacao"
                  type="number"
                  placeholder="Ex: 1"
                  value={filtroColocacao}
                  onChange={(e) => setFiltroColocacao(e.target.value)}
                  data-testid="filtro-colocacao"
                />
              </div>

              {/* Filtro UF */}
              <div>
                <Label htmlFor="filtro-uf" className="text-sm font-medium mb-2 block">
                  UF
                </Label>
                <Input
                  id="filtro-uf"
                  placeholder="Ex: SP"
                  value={filtroUF}
                  onChange={(e) => setFiltroUF(e.target.value.toUpperCase())}
                  maxLength={2}
                  data-testid="filtro-uf"
                />
              </div>

              {/* Filtro Faixa Etária */}
              <div>
                <Label htmlFor="filtro-faixa" className="text-sm font-medium mb-2 block">
                  Faixa Etária
                </Label>
                <Select value={filtroFaixa || "all"} onValueChange={(v) => setFiltroFaixa(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="filtro-faixa">
                    <SelectValue placeholder="Todas as faixas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as faixas</SelectItem>
                    {faixasDisponiveis.map((faixa) => (
                      <SelectItem key={faixa} value={faixa}>{faixa}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Filtro Equipe */}
              <div>
                <Label htmlFor="filtro-equipe" className="text-sm font-medium mb-2 block">
                  Equipe
                </Label>
                <Select value={filtroEquipe || "all"} onValueChange={(v) => setFiltroEquipe(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="filtro-equipe">
                    <SelectValue placeholder="Todas as equipes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as equipes</SelectItem>
                    {equipesDisponiveis.map((equipe) => (
                      <SelectItem key={equipe.nome} value={equipe.nome}>{equipe.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Filtro Cidade */}
              <div>
                <Label htmlFor="filtro-cidade" className="text-sm font-medium mb-2 block">
                  Cidade
                </Label>
                <Input
                  id="filtro-cidade"
                  placeholder="Ex: São Paulo"
                  value={filtroCidade}
                  onChange={(e) => setFiltroCidade(e.target.value)}
                  data-testid="filtro-cidade"
                />
              </div>

              {/* Botões */}
              <div className="space-y-2 pt-2">
                <Button 
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold"
                  data-testid="btn-filtrar"
                >
                  Filtrar
                </Button>
                <Button 
                  variant="outline"
                  className="w-full"
                  onClick={limparFiltros}
                  data-testid="btn-limpar"
                >
                  Limpar Filtros
                </Button>
              </div>

              {/* Botão Mostrar/Ocultar Destaques */}
              <Button 
                variant="outline"
                className={`w-full ${showDestaques ? 'bg-emerald-50 border-emerald-300' : ''}`}
                onClick={() => setShowDestaques(!showDestaques)}
                data-testid="btn-toggle-destaques"
              >
                <Flame className="w-4 h-4 mr-2" />
                {showDestaques ? 'Ocultar Destaques' : 'Ver Destaques'}
              </Button>

              {/* Botão Como funciona? */}
              <Dialog>
                <DialogTrigger asChild>
                  <Button 
                    variant="outline"
                    className="w-full bg-yellow-400 hover:bg-yellow-500 text-slate-900 font-semibold border-0"
                    data-testid="btn-como-funciona"
                  >
                    <HelpCircle className="w-4 h-4 mr-2" />
                    Como funciona?
                  </Button>
                </DialogTrigger>

              {/* Botão Regulamento */}
              <RegulamentoButton 
                className="w-full bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-500 font-semibold border-emerald-500/30"
                variant="outline"
                size="default"
              />
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-bold text-emerald-600">Como funciona o Ranking?</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 text-slate-700 dark:text-slate-300">
                    <div>
                      <h3 className="font-semibold text-lg mb-2">Sistema de Pontuação</h3>
                      <p>Os atletas acumulam pontos ao participar de corridas oficiais. A pontuação varia de acordo com a colocação.</p>
                      <ul className="list-disc list-inside mt-2 text-sm">
                        <li><strong>Normal:</strong> 1º lugar = 10pts, 2º = 9pts, ... até 10º = 1pt</li>
                        <li><strong>PCD/Cadeirante:</strong> 1º = 10pts, 2º = 9pts, 3º = 8pts</li>
                      </ul>
                    </div>
                    
                    <div>
                      <h3 className="font-semibold text-lg mb-2">Selo "P" (Pendente)</h3>
                      <p>O selo laranja "P" indica que o atleta ainda não completou o mínimo de provas:</p>
                      <ul className="list-disc list-inside mt-2 text-sm">
                        <li><strong>Normal:</strong> 12 provas</li>
                        <li><strong>PCD/Cadeirante:</strong> 8 provas</li>
                      </ul>
                    </div>
                    
                    <div>
                      <h3 className="font-semibold text-lg mb-2">Status Elite</h3>
                      <p>Atletas com <strong>100 pontos ou mais</strong> recebem o status Elite.</p>
                    </div>

                    <div>
                      <h3 className="font-semibold text-lg mb-2">Rankings por Período</h3>
                      <p>Além do ranking anual, temos:</p>
                      <ul className="list-disc list-inside mt-2 text-sm">
                        <li><strong>Ranking Semanal:</strong> Top 10 da última semana</li>
                        <li><strong>Ranking Mensal:</strong> Top 10 do mês atual</li>
                        <li><strong>Destaque do Mês:</strong> Atletas mais ativos e com mais pontos</li>
                      </ul>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>

          {/* Conteúdo Principal */}
          <div className="lg:col-span-3 space-y-6">
            {/* Seção de Destaques */}
            {showDestaques && (
              <RankingDestaques categoria={categoriaAtual} />
            )}

            {/* Tabela de Ranking */}
            <Card className="border-slate-200 dark:border-slate-800 shadow-lg">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-xl flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-emerald-500" />
                  Ranking Anual {categoriaAtual.replace('-', ' ').toUpperCase()}
                  <span className="ml-2 text-sm font-normal text-slate-600 dark:text-slate-400">
                    ({rankingFiltrado.length} atletas)
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-12 text-slate-600 dark:text-slate-400">
                    Carregando ranking...
                  </div>
                ) : (
                  <>
                    <RankingTable data={rankingFiltrado} onAtletaClick={handleAtletaClick} modalidade="profissional" />
                    {hasMore && !filtroNome && !filtroColocacao && !filtroUF && (
                      <LoadMoreButton
                        current={rankingData.length}
                        total={totalAtletas}
                        loading={loadingMore}
                        onClick={handleLoadMore}
                        testId="btn-carregar-mais-prof"
                        color="emerald"
                      />
                    )}
                    {!hasMore && rankingData.length > 0 && !filtroNome && !filtroColocacao && !filtroUF && (
                      <p className="text-center text-sm text-slate-400 mt-3">Mostrando todos os {rankingData.length} atletas</p>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
          </>
    </>
  );
};

export default RankingProfissional;
